import { useState, useEffect, Fragment } from 'react';
import {
  Shield, Trash2, RotateCcw, UserCheck, UserX, Search, FileText, Save,
  ChevronDown, ChevronRight, CreditCard, Users as UsersIcon, Activity, Lock, GraduationCap, LogIn, Check, Sparkles,
} from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';
import AdminTeamModal from './AdminTeamModal';
import { ToggleSwitch } from './ToggleSwitch';

export interface AdminUser {
  id: number;
  email: string;
  contactPerson: string;
  phone?: string;
  isAdmin: boolean;
  jobTitle?: string;
  companyName?: string;
  plan: string;
  planExpiresAt: string | null;
  reportCredits: number;
  tariffReportCredits: number;
  addonStartklar: boolean;
  addonStartklarExpiresAt: string | null;
  templatesEnabled: boolean;
  /** null = kein Override (Tarif entscheidet), true/false = erzwungen an/aus. */
  teamFeatureOverride: boolean | null;
  maxReportsOverride: number | null;
  maxSeatsOverride: number | null;
  /** null = Bestandsteam ohne Zuordnung (Legacy-Verhalten). */
  billingInterval: 'monthly' | 'annual' | null;
  teamOptionActive: boolean;
  blockedUntil: string | null;
  blockReason: string | null;
  createdAt: string;
  deletedAt: string | null;
  hardDeleteAt: string | null;
  customerCount: number;
  reportCount: number;
  tokenStats: { calls: number; inputTokens: number; outputTokens: number };
  teamSeats: { used: number; max: number };
  teamId: number | null;
  teamRole: string | null;
  teamName: string | null;
}

interface SubStatus {
  effectivePlan: string;
  planExpiresAt: string | null;
  addons: { startklar: boolean; startklarExpiresAt: string | null };
  reportUsage:
    | { mode: 'total' | 'monthly'; used: number; limit: number; credits: number }
    | { mode: 'unlimited'; used: number; credits: number }
    | { mode: 'pool'; used: number; available: number; tariffCredits: number; credits: number };
  features: { esf: boolean; templates: boolean; pdfExport: boolean; docxExport: boolean; team: boolean };
}

export const PLANS = [
  // Free bleibt der aktive Default für Neuanmeldungen (1 Testbericht, kein Abo).
  { value: 'free', label: 'Free', color: 'bg-slate-100 text-slate-600' },
  // Neue Tarife (Pricing-Umstellung 2026-10)
  { value: 'starter', label: 'Starter', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'professional', label: 'Professional', color: 'bg-blue-100 text-blue-700' },
  { value: 'business', label: 'Business', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'flat', label: 'Flat', color: 'bg-purple-100 text-purple-700' },
  // Kostenlos, nicht verkäuflich — nur hier vom Admin zuweisbar.
  { value: 'partner', label: 'Partner', color: 'bg-amber-100 text-amber-700' },
  // Pro/Unlimited bewusst NICHT mehr im Dropdown: letztes Bestandsteam (Demo-Mandant,
  // Team 12) wurde 2026-10-07 auf Partner migriert. Die Codes selbst bleiben in
  // tariffs.config.ts bestehen (werden intern noch als Legacy-Fallback für die
  // Startklar-Zubuchung (addonStartklar) verwendet, siehe plan.service.ts) — hier im
  // Admin-UI sind sie aber nicht mehr neu zuweisbar.
];

/** Wie lange ein frisch registrierter Nutzer im Admin als "Neu" markiert bleibt. */
const NEW_USER_DAYS = 3;
function isNewUser(createdAt: string): boolean {
  return Date.now() - new Date(createdAt).getTime() < NEW_USER_DAYS * 24 * 60 * 60 * 1000;
}

export function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

interface UsersTabProps {
  onUsersLoaded?: (users: AdminUser[]) => void;
  setError: (msg: string) => void;
}

export default function UsersTab({ onUsersLoaded, setError }: UsersTabProps) {
  const { darkMode } = useDarkMode();
  const { user } = useAuth();

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AdminUser | null>(null);
  const [expandedUser, setExpandedUser] = useState<number | null>(null);
  const [userSubStatus, setUserSubStatus] = useState<Record<number, SubStatus>>({});
  const [subStatusLoading, setSubStatusLoading] = useState<number | null>(null);
  // Edit state per user
  const [editSub, setEditSub] = useState<Record<number, {
    plan: string; planExpiresAt: string; reportCredits: number; tariffReportCredits: number;
    addonStartklar: boolean; addonStartklarExpiresAt: string;
    templatesEnabled: boolean;
    teamFeatureOverride: boolean | null;
    maxReportsOverride: string; maxSeatsOverride: string;
    billingInterval: 'monthly' | 'annual' | '';
    teamOptionActive: boolean;
    email: string; phone: string;
    blockedUntil: string; blockReason: string;
  }>>({});
  const [teamModalUser, setTeamModalUser] = useState<AdminUser | null>(null);
  const [startklarLinkUser, setStartklarLinkUser] = useState<AdminUser | null>(null);
  const [startklarEmail, setStartklarEmail] = useState('');
  const [startklarSending, setStartklarSending] = useState(false);
  const [startklarMessage, setStartklarMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const sendStartklarLink = async () => {
    if (!startklarLinkUser) return;
    setStartklarSending(true);
    setStartklarMessage(null);
    try {
      await api.post('/api/billing/admin/send-startklar-link', { userId: startklarLinkUser.id, email: startklarEmail });
      setStartklarMessage({ type: 'success', text: `Buchungs-Link wurde an ${startklarEmail} versendet.` });
      setTimeout(() => { setStartklarLinkUser(null); setStartklarMessage(null); }, 1800);
    } catch (err: any) {
      setStartklarMessage({ type: 'error', text: err?.message ?? 'Fehler beim Versenden' });
    } finally {
      setStartklarSending(false);
    }
  };

  const handleStartklarToggle = (u: AdminUser, checked: boolean) => {
    setEditSub(p => ({ ...p, [u.id]: { ...p[u.id], addonStartklar: checked } }));
    const wasActive = u.addonStartklar;
    if (checked && !wasActive) {
      setStartklarEmail(u.email);
      setStartklarMessage(null);
      setStartklarLinkUser(u);
    }
  };

  useEffect(() => {
    void loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await api.get<AdminUser[]>('/api/auth/admin/users');
      setUsers(data);
      onUsersLoaded?.(data);
    } catch {
      setError('Fehler beim Laden der Benutzer');
    } finally {
      setLoading(false);
    }
  };

  // Einmaligen Login-Link erzeugen und in die Zwischenablage kopieren (30 Min, 1x nutzbar).
  const [copiedLinkUser, setCopiedLinkUser] = useState<number | null>(null);
  const handleCopyLoginLink = async (u: AdminUser) => {
    setActionLoading(u.id);
    try {
      const { token } = await api.post<{ token: string; expiresAt: string }>(`/api/auth/admin/users/${u.id}/impersonation-link`, {});
      const link = `${window.location.origin}/login-link/${token}`;
      await navigator.clipboard.writeText(link);
      setCopiedLinkUser(u.id);
      setTimeout(() => setCopiedLinkUser(c => (c === u.id ? null : c)), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Login-Link konnte nicht erstellt werden');
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleAdmin = async (u: AdminUser) => {
    setActionLoading(u.id);
    try {
      const updated = await api.patch<AdminUser>(`/api/auth/admin/users/${u.id}`, { isAdmin: !u.isAdmin });
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, ...updated } : x));
    } catch {
      setError('Fehler beim Aktualisieren');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdatePlan = async (u: AdminUser, plan: string, planExpiresAt?: string) => {
    setActionLoading(u.id);
    try {
      const updated = await api.patch<AdminUser>(`/api/auth/admin/users/${u.id}`, { plan, planExpiresAt: planExpiresAt || null });
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, ...updated } : x));
    } catch {
      setError('Fehler beim Aktualisieren des Tarifs');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (u: AdminUser) => {
    setActionLoading(u.id);
    setConfirmDelete(null);
    try {
      await api.delete(`/api/auth/admin/users/${u.id}`);
      await loadUsers();
    } catch {
      setError('Fehler beim Löschen');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRestore = async (u: AdminUser) => {
    setActionLoading(u.id);
    try {
      await api.post(`/api/auth/admin/users/${u.id}/restore`);
      await loadUsers();
    } catch {
      setError('Fehler beim Wiederherstellen');
    } finally {
      setActionLoading(null);
    }
  };

  const toggleUserExpand = async (u: AdminUser) => {
    if (expandedUser === u.id) { setExpandedUser(null); return; }
    setExpandedUser(u.id);
    if (!userSubStatus[u.id]) {
      setSubStatusLoading(u.id);
      try {
        const data = await api.get<SubStatus>(`/api/auth/admin/users/${u.id}/subscription-status`);
        setUserSubStatus(prev => ({ ...prev, [u.id]: data }));
      } catch { /* ignore */ } finally { setSubStatusLoading(null); }
    }
    if (!editSub[u.id]) {
      setEditSub(prev => ({
        ...prev,
        [u.id]: {
          plan: u.plan ?? 'free',
          planExpiresAt: u.planExpiresAt ? u.planExpiresAt.slice(0, 10) : '',
          reportCredits: u.reportCredits ?? 0,
          tariffReportCredits: u.tariffReportCredits ?? 0,
          addonStartklar: u.addonStartklar ?? false,
          addonStartklarExpiresAt: u.addonStartklarExpiresAt ? u.addonStartklarExpiresAt.slice(0, 10) : '',
          templatesEnabled: u.templatesEnabled ?? false,
          teamFeatureOverride: u.teamFeatureOverride ?? null,
          maxReportsOverride: u.maxReportsOverride != null ? String(u.maxReportsOverride) : '',
          maxSeatsOverride: u.maxSeatsOverride != null ? String(u.maxSeatsOverride) : '',
          billingInterval: u.billingInterval ?? '',
          teamOptionActive: u.teamOptionActive ?? false,
          email: u.email ?? '',
          phone: u.phone ?? '',
          blockedUntil: u.blockedUntil ? u.blockedUntil.slice(0, 16) : '',
          blockReason: u.blockReason ?? '',
        },
      }));
    }
  };

  const saveUserSub = async (u: AdminUser) => {
    const e = editSub[u.id];
    if (!e) return;
    setActionLoading(u.id);
    try {
      const updated = await api.patch<AdminUser>(`/api/auth/admin/users/${u.id}`, {
        plan: e.plan,
        planExpiresAt: e.planExpiresAt ? new Date(e.planExpiresAt).toISOString() : null,
        reportCredits: Number(e.reportCredits),
        tariffReportCredits: Number(e.tariffReportCredits),
        addonStartklar: e.addonStartklar,
        addonStartklarExpiresAt: e.addonStartklarExpiresAt ? new Date(e.addonStartklarExpiresAt).toISOString() : null,
        templatesEnabled: e.templatesEnabled,
        teamFeatureOverride: e.teamFeatureOverride,
        maxReportsOverride: e.maxReportsOverride !== '' ? Number(e.maxReportsOverride) : null,
        maxSeatsOverride: e.maxSeatsOverride !== '' ? Number(e.maxSeatsOverride) : null,
        billingInterval: e.billingInterval || null,
        teamOptionActive: e.teamOptionActive,
        email: e.email || undefined,
        phone: e.phone || undefined,
        blockedUntil: e.blockedUntil ? new Date(e.blockedUntil).toISOString() : null,
        blockReason: e.blockReason || null,
      });
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, ...updated } : x));
      // Sync editSub with server response so stale values don't overwrite next save
      setEditSub(prev => ({
        ...prev,
        [u.id]: {
          plan: updated.plan ?? 'free',
          planExpiresAt: updated.planExpiresAt ? updated.planExpiresAt.slice(0, 10) : '',
          reportCredits: updated.reportCredits ?? 0,
          tariffReportCredits: updated.tariffReportCredits ?? 0,
          addonStartklar: updated.addonStartklar ?? false,
          addonStartklarExpiresAt: updated.addonStartklarExpiresAt ? updated.addonStartklarExpiresAt.slice(0, 10) : '',
          templatesEnabled: updated.templatesEnabled ?? false,
          teamFeatureOverride: updated.teamFeatureOverride ?? null,
          maxReportsOverride: updated.maxReportsOverride != null ? String(updated.maxReportsOverride) : '',
          maxSeatsOverride: updated.maxSeatsOverride != null ? String(updated.maxSeatsOverride) : '',
          billingInterval: updated.billingInterval ?? '',
          teamOptionActive: updated.teamOptionActive ?? false,
          email: updated.email ?? '',
          phone: updated.phone ?? '',
          blockedUntil: updated.blockedUntil ? updated.blockedUntil.slice(0, 16) : '',
          blockReason: updated.blockReason ?? '',
        },
      }));
      // Refresh subscription status
      const data = await api.get<SubStatus>(`/api/auth/admin/users/${u.id}/subscription-status`);
      setUserSubStatus(prev => ({ ...prev, [u.id]: data }));
    } catch { setError('Fehler beim Speichern'); } finally { setActionLoading(null); }
  };

  // Constants used by JSX
  const card = darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';

  const filtered = users.filter(u =>
    `${u.companyName || ''} ${u.contactPerson} ${u.email} ${u.phone || ''}`.toLowerCase().includes(search.toLowerCase())
  );

  // Team-Mitglieder direkt unter ihrem Owner einsortieren (statt verstreut in der
  // chronologischen Gesamtliste) — macht Teamzugehörigkeit auf den ersten Blick klar.
  const grouped: AdminUser[] = [];
  {
    const placed = new Set<number>();
    for (const u of filtered) {
      if (placed.has(u.id)) continue;
      if (u.teamId) {
        const teamUsers = filtered.filter(x => x.teamId === u.teamId);
        const owner = teamUsers.find(x => x.teamRole === 'owner');
        const members = teamUsers
          .filter(x => x.teamRole !== 'owner')
          .sort((a, b) => a.contactPerson.localeCompare(b.contactPerson));
        const ordered = owner ? [owner, ...members] : teamUsers;
        for (const m of ordered) {
          if (!placed.has(m.id)) { grouped.push(m); placed.add(m.id); }
        }
      } else {
        grouped.push(u);
        placed.add(u.id);
      }
    }
  }

  return (
    <>
      {/* Sperrungs-Banner */}
      {(() => {
        const blocked = users.filter(u => u.blockedUntil && new Date(u.blockedUntil) > new Date() && !u.deletedAt);
        if (blocked.length === 0) return null;
        return (
          <div className={`rounded-xl border mb-6 p-4 ${darkMode ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'}`}>
            <div className="flex items-center gap-2 mb-2">
              <Lock className={`w-4 h-4 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
              <p className={`text-sm font-semibold ${darkMode ? 'text-red-300' : 'text-red-800'}`}>
                {blocked.length} {blocked.length === 1 ? 'Benutzer ist' : 'Benutzer sind'} gesperrt
              </p>
            </div>
            <div className="space-y-1.5">
              {blocked.map(u => (
                <div key={u.id} className={`flex items-start justify-between gap-3 text-sm ${darkMode ? 'text-red-200' : 'text-red-700'}`}>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium">{u.companyName || u.contactPerson}</span>
                    <span className={`ml-2 ${darkMode ? 'text-red-300/70' : 'text-red-600/80'}`}>({u.email})</span>
                    {u.blockReason && <span className="block text-xs mt-0.5 italic">Grund: {u.blockReason}</span>}
                  </div>
                  <span className={`text-xs whitespace-nowrap ${darkMode ? 'text-red-300/70' : 'text-red-600/80'}`}>
                    bis {new Date(u.blockedUntil!).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Suchleiste */}
      <div className={`flex items-center gap-2 px-4 py-2 rounded-xl border mb-6 ${card}`}>
        <Search className="w-4 h-4 text-slate-400" />
        <input
          type="text"
          placeholder="Benutzer suchen..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 bg-transparent outline-none text-sm"
        />
      </div>

      {/* Benutzerliste */}
      <div className={`rounded-xl border overflow-x-auto ${card}`}>
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className={`border-b ${darkMode ? 'border-slate-700 bg-slate-700/50' : 'border-slate-200 bg-slate-50'}`}>
              <th className="px-4 py-3 text-left font-semibold">Firma</th>
              <th className="px-4 py-3 text-left font-semibold">Ansprechpartner</th>
              <th className="px-4 py-3 text-center font-semibold">Admin</th>
              <th className="px-4 py-3 text-center font-semibold">Tarif</th>
              <th className="px-4 py-3 text-center font-semibold">Status</th>
              <th className="px-4 py-3 text-center font-semibold">Aktionen</th>
            </tr>
          </thead>
          <tbody className={`divide-y ${darkMode ? 'divide-slate-700' : 'divide-slate-200'}`}>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Lädt...</td></tr>
            ) : grouped.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Keine Benutzer gefunden</td></tr>
            ) : grouped.map(u => {
              const isTeamMember = !!u.teamId && u.teamRole !== 'owner';
              return (
              <Fragment key={u.id}>
              <tr className={`transition-colors ${u.deletedAt ? (darkMode ? 'opacity-50' : 'opacity-40') : (!u.deletedAt && isNewUser(u.createdAt) ? (darkMode ? 'bg-amber-900/10' : 'bg-amber-50') : '')} ${darkMode ? 'hover:bg-slate-700/30' : 'hover:bg-slate-50'}`}>
                <td className={`px-4 py-3 font-medium ${isTeamMember ? 'pl-8' : ''}`}>
                  {isTeamMember && <span className={`mr-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>↳</span>}
                  {isTeamMember ? (u.companyName || u.teamName || '–') : (u.companyName || '–')}
                  {!u.deletedAt && isNewUser(u.createdAt) && (
                    <span className="ml-2 inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium" title={`Registriert am ${new Date(u.createdAt).toLocaleDateString('de-DE')}`}>
                      <Sparkles className="w-3 h-3" />Neu
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {u.contactPerson}
                  {u.id === user?.id && <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Ich</span>}
                  {u.teamRole === 'owner' && u.teamSeats.max > 1 && (
                    <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full font-medium ${darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                      Owner · {u.teamSeats.used}/{u.teamSeats.max}
                    </span>
                  )}
                  {isTeamMember && (
                    <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full font-medium ${darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                      {u.teamRole === 'admin' ? 'Team-Admin' : 'Mitglied'}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  {u.isAdmin
                    ? <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium"><Shield className="w-3 h-3" />Admin</span>
                    : <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>–</span>
                  }
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`text-xs rounded-full px-2 py-0.5 font-medium ${PLANS.find(p => p.value === (u.plan || 'free'))?.color ?? 'bg-slate-100 text-slate-600'}`}>
                    {PLANS.find(p => p.value === (u.plan || 'free'))?.label ?? u.plan}
                  </span>
                  {u.planExpiresAt && (
                    <p className={`text-[12px] mt-0.5 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      bis {new Date(u.planExpiresAt).toLocaleDateString('de-DE')}
                    </p>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  {u.deletedAt
                    ? <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">Gelöscht</span>
                    : (u.blockedUntil && new Date(u.blockedUntil) > new Date())
                      ? <span className="inline-flex items-center gap-1 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium" title={`Gesperrt bis ${new Date(u.blockedUntil).toLocaleString('de-DE')}`}><Lock className="w-3 h-3" />Gesperrt</span>
                      : <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Aktiv</span>
                  }
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {u.id !== user?.id && !u.deletedAt && (
                        <button
                          onClick={() => handleToggleAdmin(u)}
                          disabled={actionLoading === u.id}
                          title={u.isAdmin ? 'Admin-Rechte entziehen' : 'Admin-Rechte geben'}
                          className={`p-1.5 rounded-lg transition-colors ${u.isAdmin ? 'text-blue-600 hover:bg-blue-100' : 'text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                        >
                          {u.isAdmin ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                        </button>
                      )}
                      {u.id !== user?.id && !u.deletedAt && !u.isAdmin && (
                        <button
                          onClick={() => handleCopyLoginLink(u)}
                          disabled={actionLoading === u.id}
                          title="Einmaligen Login-Link kopieren (30 Min, 1× nutzbar)"
                          className={`p-1.5 rounded-lg transition-colors ${copiedLinkUser === u.id ? 'text-green-600 bg-green-50' : 'text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                        >
                          {copiedLinkUser === u.id ? <Check className="w-4 h-4" /> : <LogIn className="w-4 h-4" />}
                        </button>
                      )}
                      {u.id !== user?.id && (
                        u.deletedAt ? (
                          <button onClick={() => handleRestore(u)} disabled={actionLoading === u.id} title="Wiederherstellen" className="p-1.5 rounded-lg text-green-600 hover:bg-green-100 transition-colors">
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        ) : (
                          <button onClick={() => setConfirmDelete(u)} disabled={actionLoading === u.id} title="Löschen" className="p-1.5 rounded-lg text-red-500 hover:bg-red-100 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )
                      )}
                    </div>
                    {!u.deletedAt && (
                      <button
                        onClick={() => setTeamModalUser(u)}
                        title="Team verwalten"
                        className={`p-1.5 rounded-lg transition-colors ${darkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-400 hover:bg-slate-100'}`}
                      >
                        <UsersIcon className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => toggleUserExpand(u)}
                      title="Tarif-Details"
                      className={`p-1.5 rounded-lg transition-colors ${expandedUser === u.id ? 'text-blue-600 bg-blue-50' : darkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-400 hover:bg-slate-100'}`}
                    >
                      {expandedUser === u.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                  </div>
                </td>
              </tr>
              {expandedUser === u.id && (() => {
                const sub = userSubStatus[u.id];
                const e = editSub[u.id];
                const isLoading = subStatusLoading === u.id;
                const usage = sub?.reportUsage;
                const usageLimit = usage ? (usage.mode === 'pool' ? usage.available : usage.mode === 'unlimited' ? Infinity : usage.limit) : 0;
                const usageUsed = usage ? (usage.mode === 'pool' ? 0 : usage.used) : 0;
                const pct = usage && Number.isFinite(usageLimit) && usageLimit > 0 ? Math.min((usageUsed / usageLimit) * 100, 100) : 0;
                const atLimit = usage ? (usage.mode === 'pool' ? usage.available <= 0 : usage.mode === 'unlimited' ? false : usageUsed >= usageLimit) : false;
                const planColor = PLANS.find(p => p.value === (sub?.effectivePlan ?? u.plan ?? 'free'))?.color ?? 'bg-slate-100 text-slate-600';
                const planLabel = PLANS.find(p => p.value === (sub?.effectivePlan ?? u.plan ?? 'free'))?.label ?? u.plan;
                const inputCls = `border rounded px-2 py-1 text-sm w-full ${darkMode ? 'border-slate-600 bg-slate-700 text-white' : 'border-slate-300 bg-white text-slate-900'}`;
                const disabledCls = 'opacity-50 cursor-not-allowed';
                // Abrechnung/Tarif-Guthaben wirken nur bei den 4 neuen bezahlten Tarifen —
                // bei Legacy (pro/unlimited) und bei Free/Partner bleiben sie wirkungslos.
                const isNewTariff = e ? ['starter', 'professional', 'business', 'flat'].includes(e.plan) : false;
                // Teamoption ist die KOSTENPFLICHTIGE Zubuchung — Free/Partner haben keine
                // Stripe-Subscription, an die sie sich anhängen könnte.
                const hasNoSubscription = e ? (e.plan === 'free' || e.plan === 'partner') : false;
                // Plan, Guthaben, Overrides etc. sind Team-Felder (wirken auf targetTeam, nicht
                // auf den einzelnen User) — über eine Mitglieder-Zeile bearbeitet sähe das wie eine
                // persönliche Änderung aus, träfe aber unsichtbar das ganze Team. Nur über die
                // Owner-Zeile bearbeitbar.
                const teamFieldsNote = 'Nur über den Team-Inhaber bearbeitbar';
                return (
                  <tr key={`${u.id}-sub`} className={darkMode ? 'bg-slate-800/60' : 'bg-slate-50'}>
                    <td colSpan={6} className="px-6 py-4">
                      {isLoading ? (
                        <p className="text-sm text-slate-400">Lädt…</p>
                      ) : (
                        <div className="flex gap-8 flex-wrap">
                          {/* Linke Seite: Ansicht */}
                          <div className="flex-1 min-w-0 md:min-w-[260px] space-y-3">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold ${planColor}`}>
                                <CreditCard className="w-3.5 h-3.5" />{planLabel}
                              </span>
                              {sub?.addons?.startklar && (
                                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold bg-emerald-100 text-emerald-700">
                                  <GraduationCap className="w-3.5 h-3.5" />Startklar-Programm{sub.addons.startklarExpiresAt ? ` bis ${new Date(sub.addons.startklarExpiresAt).toLocaleDateString('de-DE')}` : ''}
                                </span>
                              )}
                            </div>
                            {usage && (
                              <div className="space-y-1">
                                {usage.mode === 'unlimited' ? (
                                  <div className="text-xs font-semibold text-emerald-500">Unbegrenzt Berichte (Flat)</div>
                                ) : (
                                  <>
                                    <div className="flex justify-between text-xs">
                                      <span className={darkMode ? 'text-slate-300' : 'text-slate-600'}>
                                        {usage.mode === 'pool' ? 'Berichte verfügbar' : usage.mode === 'monthly' ? 'Berichte diesen Monat' : 'Berichte insgesamt'}
                                      </span>
                                      <span className={`font-semibold ${atLimit ? 'text-red-500' : ''}`}>
                                        {usage.mode === 'pool' ? usage.available : `${usage.used} / ${usage.limit}`}
                                      </span>
                                    </div>
                                    <div className={`w-full rounded-full h-1.5 ${darkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
                                      <div className={`h-1.5 rounded-full ${atLimit ? 'bg-red-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                                    </div>
                                    {atLimit && <p className="text-xs text-red-500">Limit erreicht</p>}
                                  </>
                                )}
                                {usage.credits > 0 && <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>+{usage.credits} gekaufte/geschenkte Credits</p>}
                              </div>
                            )}
                            {sub?.features && (
                              <div className="grid grid-cols-2 gap-1">
                                {([['esf','ESF-Fragebogen'],['templates','Vorlagen'],['pdfExport','PDF-Export'],['docxExport','Word-Export'],['team','Team']] as [keyof SubStatus['features'], string][]).map(([k, l]) => (
                                  <div key={k} className={`flex items-center gap-1.5 text-xs ${sub.features[k] ? (darkMode ? 'text-slate-300' : 'text-slate-700') : (darkMode ? 'text-slate-600' : 'text-slate-400')}`}>
                                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${sub.features[k] ? 'bg-green-500' : (darkMode ? 'bg-slate-600' : 'bg-slate-300')}`} />
                                    {l}
                                  </div>
                                ))}
                              </div>
                            )}
                            {/* Aktivitäts-Stats */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2">
                              <div className={`rounded-lg px-3 py-2 ${darkMode ? 'bg-slate-700/50' : 'bg-white border border-slate-200'}`}>
                                <div className={`flex items-center gap-1 text-[12px] uppercase tracking-wide ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                  <UsersIcon className="w-3 h-3" />Kunden
                                </div>
                                <p className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>{u.customerCount ?? 0}</p>
                              </div>
                              <div className={`rounded-lg px-3 py-2 ${darkMode ? 'bg-slate-700/50' : 'bg-white border border-slate-200'}`}>
                                <div className={`flex items-center gap-1 text-[12px] uppercase tracking-wide ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                  <FileText className="w-3 h-3" />Berichte
                                </div>
                                <p className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>{u.reportCount ?? 0}</p>
                              </div>
                              <div className={`rounded-lg px-3 py-2 ${darkMode ? 'bg-slate-700/50' : 'bg-white border border-slate-200'}`}>
                                <div className={`flex items-center gap-1 text-[12px] uppercase tracking-wide ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                  <Activity className="w-3 h-3" />Tokens
                                </div>
                                <p className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`} title={`${u.tokenStats?.calls ?? 0} Calls · In ${u.tokenStats?.inputTokens ?? 0} · Out ${u.tokenStats?.outputTokens ?? 0}`}>
                                  {fmtTokens((u.tokenStats?.inputTokens ?? 0) + (u.tokenStats?.outputTokens ?? 0))}
                                </p>
                                <p className={`text-[12px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{u.tokenStats?.calls ?? 0} Calls</p>
                              </div>
                              <div className={`rounded-lg px-3 py-2 ${darkMode ? 'bg-slate-700/50' : 'bg-white border border-slate-200'}`}>
                                <div className={`flex items-center gap-1 text-[12px] uppercase tracking-wide ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                  <UsersIcon className="w-3 h-3" />Team
                                </div>
                                <p className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                                  {u.teamSeats?.used ?? 1} / {u.teamSeats?.max ?? 1}
                                </p>
                                <p className={`text-[12px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Plätze</p>
                              </div>
                            </div>

                            {/* Sperre */}
                            {e && (
                              <div className={`rounded-lg p-3 mt-2 ${darkMode ? 'bg-slate-700/40' : 'bg-white border border-slate-200'}`}>
                                <div className="flex items-center gap-2 mb-2">
                                  <Lock className={`w-4 h-4 ${e.blockedUntil && new Date(e.blockedUntil) > new Date() ? 'text-red-500' : (darkMode ? 'text-slate-400' : 'text-slate-500')}`} />
                                  <label className={`text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Benutzer sperren</label>
                                  {e.blockedUntil && (
                                    <button
                                      type="button"
                                      onClick={() => setEditSub(p => ({ ...p, [u.id]: { ...p[u.id], blockedUntil: '', blockReason: '' } }))}
                                      className="ml-auto text-xs text-blue-600 hover:underline"
                                    >
                                      Sperre entfernen
                                    </button>
                                  )}
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                                  <div>
                                    <label className={`block text-xs mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Gesperrt bis</label>
                                    <input
                                      type="datetime-local"
                                      className={inputCls}
                                      value={e.blockedUntil}
                                      onChange={ev => setEditSub(p => ({ ...p, [u.id]: { ...p[u.id], blockedUntil: ev.target.value } }))}
                                    />
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    <label className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Schnell-Sperre</label>
                                    <div className="flex gap-1">
                                      {([
                                        { label: '1h', mins: 60 },
                                        { label: '24h', mins: 60 * 24 },
                                        { label: '7T', mins: 60 * 24 * 7 },
                                      ] as const).map(({ label, mins }) => (
                                        <button
                                          key={label}
                                          type="button"
                                          onClick={() => {
                                            const dt = new Date(Date.now() + mins * 60 * 1000);
                                            const local = new Date(dt.getTime() - dt.getTimezoneOffset() * 60 * 1000).toISOString().slice(0, 16);
                                            setEditSub(p => ({ ...p, [u.id]: { ...p[u.id], blockedUntil: local } }));
                                          }}
                                          className={`flex-1 px-2 py-1 rounded text-xs font-medium ${darkMode ? 'bg-slate-600 hover:bg-slate-500 text-white' : 'bg-white border border-slate-300 hover:bg-slate-100 text-slate-700'}`}
                                        >
                                          {label}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                                <input
                                  type="text"
                                  placeholder="Grund (optional, z.B. Spam, ToS-Verstoß)"
                                  className={inputCls}
                                  value={e.blockReason}
                                  onChange={ev => setEditSub(p => ({ ...p, [u.id]: { ...p[u.id], blockReason: ev.target.value } }))}
                                />
                              </div>
                            )}
                          </div>

                          {/* Rechte Seite: Bearbeitung */}
                          {e && (
                            <div className="flex-1 min-w-0 md:min-w-[280px] space-y-2">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <div>
                                  <label className={`block text-xs mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>E-Mail</label>
                                  <input type="email" className={inputCls} value={e.email} onChange={ev => setEditSub(p => ({ ...p, [u.id]: { ...p[u.id], email: ev.target.value } }))} />
                                </div>
                                <div>
                                  <label className={`block text-xs mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Telefon</label>
                                  <input type="text" className={inputCls} value={e.phone} onChange={ev => setEditSub(p => ({ ...p, [u.id]: { ...p[u.id], phone: ev.target.value } }))} />
                                </div>
                                <div>
                                  <label className={`block text-xs mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    Plan{isTeamMember && <span className="font-normal"> ({teamFieldsNote})</span>}
                                  </label>
                                  <select
                                    className={`${inputCls} ${isTeamMember ? disabledCls : ''}`}
                                    disabled={isTeamMember}
                                    value={e.plan}
                                    onChange={ev => setEditSub(p => ({ ...p, [u.id]: { ...p[u.id], plan: ev.target.value } }))}
                                  >
                                    {PLANS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <label className={`block text-xs mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    Plan läuft ab{isTeamMember && <span className="font-normal"> ({teamFieldsNote})</span>}
                                  </label>
                                  <input
                                    type="date"
                                    className={`${inputCls} ${isTeamMember ? disabledCls : ''}`}
                                    disabled={isTeamMember}
                                    value={e.planExpiresAt}
                                    onChange={ev => setEditSub(p => ({ ...p, [u.id]: { ...p[u.id], planExpiresAt: ev.target.value } }))}
                                  />
                                </div>
                                <div>
                                  <label className={`block text-xs mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    Abrechnung{isTeamMember ? <span className="font-normal"> ({teamFieldsNote})</span> : !isNewTariff && <span className="font-normal"> (nur bei neuen Tarifen)</span>}
                                  </label>
                                  <select
                                    className={`${inputCls} ${(!isNewTariff || isTeamMember) ? disabledCls : ''}`}
                                    disabled={!isNewTariff || isTeamMember}
                                    value={e.billingInterval}
                                    onChange={ev => setEditSub(p => ({ ...p, [u.id]: { ...p[u.id], billingInterval: ev.target.value as 'monthly' | 'annual' | '' } }))}
                                  >
                                    <option value="">Legacy (keine Zuordnung)</option>
                                    <option value="monthly">Monatlich — kein Rollover</option>
                                    <option value="annual">Jährlich — Rollover bis 3 Monate</option>
                                  </select>
                                </div>
                                <div>
                                  <label className={`block text-xs mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    Bericht-Credits (gekauft/geschenkt){isTeamMember && <span className="font-normal"> ({teamFieldsNote})</span>}
                                  </label>
                                  <input
                                    type="number" min={0}
                                    className={`${inputCls} ${isTeamMember ? disabledCls : ''}`}
                                    disabled={isTeamMember}
                                    value={e.reportCredits}
                                    onChange={ev => setEditSub(p => ({ ...p, [u.id]: { ...p[u.id], reportCredits: Number(ev.target.value) } }))}
                                  />
                                </div>
                                <div>
                                  <label className={`block text-xs mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    Tarif-Guthaben (nur neue Tarife){isTeamMember && <span className="font-normal"> ({teamFieldsNote})</span>}
                                  </label>
                                  <input
                                    type="number" min={0}
                                    className={`${inputCls} ${(!isNewTariff || isTeamMember) ? disabledCls : ''}`}
                                    disabled={!isNewTariff || isTeamMember}
                                    value={e.tariffReportCredits}
                                    onChange={ev => setEditSub(p => ({ ...p, [u.id]: { ...p[u.id], tariffReportCredits: Number(ev.target.value) } }))}
                                  />
                                </div>
                                <div>
                                  <label className={`block text-xs mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    Max Berichte Override{isTeamMember && <span className="font-normal"> ({teamFieldsNote})</span>}
                                  </label>
                                  <input
                                    type="number" min={0} placeholder="Standard"
                                    className={`${inputCls} ${isTeamMember ? disabledCls : ''}`}
                                    disabled={isTeamMember}
                                    value={e.maxReportsOverride}
                                    onChange={ev => setEditSub(p => ({ ...p, [u.id]: { ...p[u.id], maxReportsOverride: ev.target.value } }))}
                                  />
                                </div>
                              </div>
                              <div className="pb-2">
                                <ToggleSwitch
                                  id={`teamoption-${u.id}`}
                                  checked={e.teamOptionActive}
                                  disabled={hasNoSubscription || isTeamMember}
                                  onChange={checked => setEditSub(p => ({ ...p, [u.id]: { ...p[u.id], teamOptionActive: checked } }))}
                                  label={`Teamoption aktiv (+50 €/Monat, 5 Sitzplätze)${isTeamMember ? ` — ${teamFieldsNote}` : hasNoSubscription ? ' — keine Subscription vorhanden' : ''}`}
                                  darkMode={darkMode}
                                />
                              </div>
                              <div className="flex items-end gap-4">
                                <div className="pb-2">
                                  <ToggleSwitch
                                    id={`startklar-${u.id}`}
                                    checked={e.addonStartklar}
                                    disabled={isTeamMember}
                                    onChange={checked => handleStartklarToggle(u, checked)}
                                    label={`Startklar-Programm aktiv${isTeamMember ? ` — ${teamFieldsNote}` : ''}`}
                                    darkMode={darkMode}
                                  />
                                </div>
                                {e.addonStartklar && (
                                  <div className="flex-1">
                                    <label className={`block text-xs mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Ablaufdatum</label>
                                    <input
                                      type="date"
                                      className={`${inputCls} ${isTeamMember ? disabledCls : ''}`}
                                      disabled={isTeamMember}
                                      value={e.addonStartklarExpiresAt}
                                      onChange={ev => setEditSub(p => ({ ...p, [u.id]: { ...p[u.id], addonStartklarExpiresAt: ev.target.value } }))}
                                    />
                                  </div>
                                )}
                              </div>
                              <div className="pb-2">
                                <ToggleSwitch
                                  id={`templates-${u.id}`}
                                  checked={e.templatesEnabled}
                                  onChange={checked => setEditSub(p => ({ ...p, [u.id]: { ...p[u.id], templatesEnabled: checked } }))}
                                  label="Templates-Feature aktiv"
                                  darkMode={darkMode}
                                />
                              </div>
                              <div className="pb-2">
                                <ToggleSwitch
                                  id={`team-${u.id}`}
                                  checked={e.teamFeatureOverride ?? !!sub?.features?.team}
                                  disabled={isTeamMember}
                                  onChange={checked => setEditSub(p => ({ ...p, [u.id]: { ...p[u.id], teamFeatureOverride: checked } }))}
                                  label={`Team-Funktion aktiv${isTeamMember ? ` — ${teamFieldsNote}` : ''}`}
                                  darkMode={darkMode}
                                />
                                {e.teamFeatureOverride !== null && (
                                  <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                    Team, Mitglieder und Daten bleiben erhalten — nur der geteilte Zugriff der Mitglieder ändert sich.{' '}
                                    <button
                                      type="button"
                                      disabled={isTeamMember}
                                      className={`text-blue-600 hover:underline ${isTeamMember ? 'opacity-50 cursor-not-allowed' : ''}`}
                                      onClick={() => setEditSub(p => ({ ...p, [u.id]: { ...p[u.id], teamFeatureOverride: null } }))}
                                    >
                                      Zurücksetzen auf automatisch
                                    </button>
                                  </p>
                                )}
                              </div>

                              <div className="flex items-center justify-between">
                                <button
                                  onClick={() => saveUserSub(u)}
                                  disabled={actionLoading === u.id}
                                  className="flex items-center gap-2 px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
                                >
                                  <Save className="w-3.5 h-3.5" />
                                  {actionLoading === u.id ? 'Speichert…' : 'Speichern'}
                                </button>
                                <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                  Erstellt: {new Date(u.createdAt).toLocaleDateString('de-DE')}
                                  {u.deletedAt && u.hardDeleteAt && (() => {
                                    const hard = new Date(u.hardDeleteAt);
                                    const daysLeft = Math.ceil((hard.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
                                    return ` · Löschung: ${hard.toLocaleDateString('de-DE')} (${daysLeft}d)`;
                                  })()}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })()}
              </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className={`mt-4 text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
        {filtered.length} Benutzer · Gelöschte Konten können wiederhergestellt werden
      </p>

      {/* Startklar-Link-Versand-Modal */}
      {startklarLinkUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => { if (!startklarSending) setStartklarLinkUser(null); }}>
          <div className={`rounded-xl border shadow-xl p-6 max-w-md mx-4 w-full ${card}`} onClick={ev => ev.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-2">Startklar-Programm: Buchungs-Link versenden?</h3>
            <p className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              Soll dem Kunden ein Stripe-Checkout-Link für das Startklar-Programm (590 €/Monat, 6 Monate, danach automatisch Pro 125 €/Monat) per E-Mail zugeschickt werden? Wenn du den Link nicht versenden möchtest, kannst du das Häkchen einfach manuell setzen (ohne Stripe).
            </p>
            <label className={`block text-xs mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>E-Mail-Adresse</label>
            <input
              type="email"
              className={`border rounded px-2 py-1.5 text-sm w-full mb-4 ${darkMode ? 'border-slate-600 bg-slate-700 text-white' : 'border-slate-300 bg-white text-slate-900'}`}
              value={startklarEmail}
              onChange={ev => setStartklarEmail(ev.target.value)}
              disabled={startklarSending}
            />
            {startklarMessage && (
              <p className={`text-sm mb-3 ${startklarMessage.type === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>
                {startklarMessage.text}
              </p>
            )}
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setStartklarLinkUser(null)}
                disabled={startklarSending}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${darkMode ? 'bg-slate-700 hover:bg-slate-600 text-slate-200' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'} disabled:opacity-50`}
              >
                Nur Häkchen setzen
              </button>
              <button
                type="button"
                onClick={() => void sendStartklarLink()}
                disabled={startklarSending || !startklarEmail}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
              >
                {startklarSending ? 'Wird versendet…' : 'Link versenden'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lösch-Bestätigung (User) */}
      {teamModalUser && (
        <AdminTeamModal
          user={teamModalUser}
          onClose={() => setTeamModalUser(null)}
          onSuccess={() => void loadUsers()}
        />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className={`rounded-xl border shadow-xl p-6 max-w-sm mx-4 ${card}`}>
            <h3 className="text-lg font-semibold mb-2">Benutzer löschen?</h3>
            <p className={`text-sm mb-6 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              Möchten Sie <strong>{confirmDelete.contactPerson}</strong> ({confirmDelete.email}) wirklich löschen? Der Account kann danach wiederhergestellt werden.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium ${darkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-700 hover:bg-slate-50'}`}
              >
                Abbrechen
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700"
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
